import { Controller } from "@hotwired/stimulus"

const DAY_IN_MS = 24 * 60 * 60 * 1000

// INTENT: Ask visitors who are leaving the pricing page why they didn't buy (exit-intent survey).
// EXPECTED OUTCOME: Opens a native <dialog> at most once per page, then stays suppressed for suppressDays per exam.
// CAVEAT/TRAP: Never use beforeunload; browsers only allow a generic native prompt there, which would block navigation.
export default class extends Controller {
  static targets = ["dialog", "trigger"]
  static values = {
    storageKey: String,
    suppressDays: { type: Number, default: 30 },
    armDelay: { type: Number, default: 3000 }
  }

  connect() {
    this.shown = false
    this.checkoutStarted = false
    this.pendingHref = null
    this.armed = false
    this.armTimer = setTimeout(() => { this.armed = true }, this.armDelayValue)
  }

  disconnect() {
    clearTimeout(this.armTimer)
  }

  // Desktop exit intent: the cursor leaves the viewport through the top edge (toward tabs/address bar).
  // CAVEAT/TRAP: Moving into the Stripe checkout iframe also fires mouseout with relatedTarget == null,
  // so the clientY check is what tells a real exit apart from entering the iframe.
  mouseout(event) {
    if (!this.armed || !this.hasFinePointer()) return
    if (event.relatedTarget || event.clientY > 0) return

    this.open("exit_intent")
  }

  // All devices: the first click on the page's Close button opens the survey, then navigates once it closes.
  interceptClose(event) {
    const link = event.target.closest?.("[data-exit-survey-close]")
    if (!link || !this.canOpen()) return

    event.preventDefault()
    this.pendingHref = link.getAttribute("href")
    this.open("close_button")
  }

  // Window blur with an iframe focused means the visitor clicked into Stripe Embedded Checkout.
  // Someone actively paying shouldn't be interrupted.
  detectCheckoutFocus() {
    setTimeout(() => {
      const active = document.activeElement
      if (active?.tagName === "IFRAME" && active.closest('[data-controller~="stripe"]')) {
        this.checkoutStarted = true
      }
    }, 0)
  }

  dismiss() {
    this.dialogTarget.close()
  }

  // Fires on dismiss, Escape, and after the thank-you Close button.
  closed() {
    if (!this.pendingHref) return

    const href = this.pendingHref
    this.pendingHref = null
    this.navigate(href)
  }

  navigate(href) {
    // url_for(:back) renders "javascript:history.back()" when there's no referrer.
    if (href.startsWith("javascript:")) {
      window.history.back()
    } else {
      window.location.assign(href)
    }
  }

  open(trigger) {
    if (!this.canOpen()) return

    this.shown = true
    if (this.hasTriggerTarget) this.triggerTarget.value = trigger
    this.rememberShown()
    this.dialogTarget.showModal()
  }

  canOpen() {
    return !this.shown &&
      !this.checkoutStarted &&
      typeof this.dialogTarget.showModal === "function" &&
      !this.recentlyShown()
  }

  recentlyShown() {
    const shownAt = this.readShownAt()
    return shownAt !== null && Date.now() - shownAt < this.suppressDaysValue * DAY_IN_MS
  }

  // CAVEAT/TRAP: localStorage throws in some private modes and when site data is blocked.
  // Falling back to this.shown still limits the survey to once per page view.
  readShownAt() {
    try {
      const value = Number(window.localStorage.getItem(this.storageKeyValue))
      return value > 0 ? value : null
    } catch {
      return null
    }
  }

  rememberShown() {
    try {
      window.localStorage.setItem(this.storageKeyValue, String(Date.now()))
    } catch {
      // Ignored; see readShownAt.
    }
  }

  hasFinePointer() {
    // Touch devices emit synthetic mouse events; only arm the cursor trigger for real pointers.
    return window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false
  }
}
